// Номиналы для полистного пересчёта кассы на ревизии: ревизор вводит количество купюр
// каждого номинала, сумму считаем сами.
//
// Списка два, и у каждого своя работа:
//   * справочник сервера (GET /CashAudit/denominations) — что бэк вообще принимает.
//     Номинал вне него отклоняется с DENOMINATION_NOT_ALLOWED, поэтому в акте такой
//     номинал сначала заводят на бэке (POST /CurrencyDenomination), а потом считают;
//   * defaultNominals — что показываем в акте по умолчанию. Это ходовые купюры, тот же
//     справочник, только без монет и мелочи, которых в кассе не бывает.

export interface Denomination {
    /** Номинал: 100 USD, 0.5 EUR и т.д. Для золота — 1 слиток. */
    nominal: number;
}

/** Один номинал — одна строка ввода: дубли схлопываем, иначе они делят общий ключ количества */
const toDenominations = (nominals: number[]): Denomination[] => (
    [...new Set(nominals || [])]
        .sort((a, b) => b - a)
        .map((nominal) => ({ nominal }))
);

/** Слиток считаем поштучно: номинал строки — один слиток. */
export const goldDenominations: Denomination[] = [{ nominal: 1 }];

export const isGoldCode = (currencyCode: string) => (currencyCode || "").toUpperCase().indexOf("GOLD") >= 0;

/** GOLD10 → 10 грамм. Вес нужен отдельной ручке пересчёта золота. */
export const goldWeightOfCode = (currencyCode: string) => +((currencyCode || "").toUpperCase().replace(/GOLD/g, "")) || 0;

let referenceDenominations: Record<string, Denomination[]> | null = null;
let goldWeightsGrams: number[] = [];

/**
 * Сервер отдаёт плоский список чисел — банкноту от монеты он не отличает вовсе,
 * ключ пересчёта это само число.
 */
export const setDenominationsReference = (reference: { currencies?: Record<string, number[]>, goldWeightsGrams?: number[] } | null) => {
    if (!reference?.currencies) {
        return;
    }

    referenceDenominations = Object.entries(reference.currencies).reduce<Record<string, Denomination[]>>((acc, [code, nominals]) => {
        acc[(code || "").toUpperCase()] = toDenominations(nominals);

        return acc;
    }, {});

    goldWeightsGrams = reference.goldWeightsGrams || [];
};

export const getGoldWeights = () => goldWeightsGrams;

export const hasDenominationsReference = () => !!referenceDenominations;

/**
 * Номиналы для валюты. Пустой список означает, что справочник не загрузился или сервер
 * не знает такой валюты, — вводить в этом случае нечего.
 */
export const getDenominations = (currencyCode: string): Denomination[] => {
    if (isGoldCode(currencyCode)) {
        return goldDenominations;
    }

    return referenceDenominations?.[(currencyCode || "").toUpperCase()] || [];
};

/**
 * Ходовые купюры по валютам: с них начинается пересчёт в акте. Список короче серверного
 * справочника намеренно — гнать ревизора через два десятка заведомо пустых строк с монетами
 * дольше, чем добавить недостающий номинал руками.
 */
const defaultNominals: Record<string, number[]> = {
    KZT: [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000],
    USD: [1, 2, 5, 10, 20, 50, 100],
    EUR: [5, 10, 20, 50, 100, 200, 500],
    CNY: [100, 50, 20, 10, 5, 1],
    ILS: [20, 50, 100, 200],
    RUB: [5, 10, 50, 100, 200, 500, 1000, 2000, 5000],
    TRY: [5, 10, 20, 50, 100, 200],
    UZS: [1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000],
    KGS: [20, 50, 100, 200, 500, 1000, 5000],
    GBP: [5, 10, 20, 50],
    AED: [5, 10, 20, 50, 100, 200, 500, 1000],
    CHF: [10, 20, 50, 100, 200, 1000],
    CAD: [5, 10, 20, 50, 100],
    AUD: [5, 10, 20, 50, 100],
    GEL: [5, 10, 20, 50, 100],
    JPY: [1000, 2000, 5000, 10000],
    CZK: [100, 200, 500, 1000, 2000, 5000],
    INR: [10, 20, 50, 100, 200, 500],
    THB: [20, 50, 100, 500, 1000],
    SAR: [5, 10, 20, 50, 100, 500],
    AZN: [1, 5, 10, 20, 50, 100, 200, 500],
    AMD: [1000, 2000, 5000, 10000, 20000, 50000],
    KRW: [1000, 5000, 10000, 50000],
    VND: [100, 200, 500, 1000, 2000, 5000, 10000, 20000, 100000, 200000, 500000],
    MXN: [20, 50, 100, 200, 500, 1000],
    SGD: [2, 5, 10, 20, 50],
    UAH: [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000],
    PLN: [10, 20, 50, 100, 200, 500],
    QAR: [1, 2, 5, 10, 50, 100, 500],
    TJS: [1, 2, 5, 10, 20, 50, 100, 200, 500],
};

/**
 * С каких номиналов открывается позиция в акте. Для валюты без своего списка показываем
 * весь серверный справочник: длинный список лучше пустой позиции, которую нечем заполнить.
 */
export const getDefaultDenominations = (currencyCode: string): Denomination[] => {
    const code = (currencyCode || "").toUpperCase();

    if (isGoldCode(code)) {
        return goldDenominations;
    }

    const own = defaultNominals[code];

    return own ? toDenominations(own) : getDenominations(code);
};
